'use client';

import { cn } from '@core/utils';
import { motion } from 'framer-motion';

import { Image } from '@/components/client';

interface ChipProps {
  className?: string;
  localName?: string;
  specialtyImageUrl?: string;
  isActive?: boolean;
  isRecent?: boolean;
}

export const Chip = ({
  className,
  localName,
  specialtyImageUrl,
  isActive = false,
  isRecent = false,
}: ChipProps) => {
  const MotionWrapper = isRecent ? motion.div : 'div';
  const motionProps = isRecent
    ? {
        animate: {
          y: [0, -8, 0],
        },
        transition: {
          duration: 0.6,
          repeat: Infinity,
          repeatDelay: 1,
          ease: 'easeOut',
        },
      }
    : {};

  return (
    <MotionWrapper className={cn('relative', className)} {...motionProps}>
      {isActive && (
        <div className="absolute left-1/2 top-[64px] h-16 w-36 -translate-x-1/2">
          <svg
            width="35"
            height="16"
            viewBox="0 0 35 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <ellipse cx="17.5" cy="8" rx="17.5" ry="8" fill="#DDF2FF" />
          </svg>
        </div>
      )}

      <div className="relative h-[75px] w-[61px]">
        <svg
          className="absolute inset-0 size-full"
          viewBox="0 0 61 75"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M34.71 70.126C32.19 72.08 28.79 72.08 26.27 70.125C21.98 66.8 15.75 61.4 10.61 54.665C5.46 47.91 1.5 39.95 1.5 31.48C1.5 19.68 4.82 12.28 9.92 7.8C15.05 3.29 22.25 1.5 30.49 1.5C38.73 1.5 45.94 3.29 51.07 7.8C56.172 12.28 59.5 19.68 59.5 31.48C59.5 39.955 55.54 47.91 50.38 54.666C45.24 61.41 39.01 66.8 34.71 70.126Z"
            fill={isActive ? '#FFF4BE' : '#F0F0F0'}
            stroke={isActive ? '#FFEE98' : '#E5E7EC'}
            strokeWidth="3"
          />
        </svg>

        {isActive ? (
          <>
            <div className="absolute left-1/2 top-4 size-44 -translate-x-1/2">
              {specialtyImageUrl ? (
                <div className="relative size-full">
                  <Image
                    src={specialtyImageUrl}
                    alt={`${localName} 특산품 이미지`}
                    fill
                    sizes="44px"
                    className="object-contain"
                    loading="lazy"
                    quality={75}
                  />
                </div>
              ) : (
                <div className="flex size-full items-center justify-center">
                  <svg
                    width="12"
                    height="30"
                    viewBox="0 0 17 44"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M0.29 7.87C-0.1 5.34 -0.1 1.79 0.29 0.27C1.7 -0.02 2.32 -0.02 3.75 0.03C11.9 0.35 16.36 4.76 16.36 12.94C16.36 18.31 13.75 22.34 9.15 24.13C9.15 26.05 9.04 28.14 8.9 30.03C6.89 30.45 2.26 30.45 0.43 30.03C0.07 25.525 -0.03 20.22 0.17 18.36L2.06 18.2C5.99 17.88 7.62 15.82 7.62 13.0C7.62 10.02 5.56 8.43 2.93 8.32C1.6 8.26 0.48 8.32 0.29 8.32ZM0.68 38.9C0.68 35.92 2.97 33.6 5.82 33.6C8.67 33.6 10.97 35.92 10.97 38.9C10.97 41.88 8.67 44.2 5.82 44.2C2.97 44.2 0.68 41.88 0.68 38.9Z"
                      fill="#FF8428"
                    />
                  </svg>
                </div>
              )}
            </div>

            <p
              className={cn(
                'absolute left-1/2 w-56 -translate-x-1/2 whitespace-pre-wrap text-center font-700 text-Gray-700 typo-caption2',
                'top-44',
              )}
            >
              {localName}
            </p>

            {isRecent && (
              <div className="absolute right-0 top-0 size-8 rounded-pill bg-O-500" />
            )}
          </>
        ) : (
          <svg
            className="absolute left-1/2 top-[16px] -translate-x-1/2"
            width="15"
            height="40"
            viewBox="0 0 15 40"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0.1 7.15C-0.03 5.42 -0.03 1.81 0.1 0.08C1.453 -0.02 1.98 -0.02 3.21 0.03C10.2 0.3 14 4.09 14 11.12C14 15.73 11.77 19.2 7.84 20.75C7.84 22.39 7.75 24.172 7.62 25.72C5.96 25.91 1.94 25.91 0.37 25.72C0.06 21.94 -0.03 17.37 0.15 15.77L1.76 15.64C5.13 15.364 6.53 13.58 6.53 11.17C6.53 8.61 4.78 7.24 2.51 7.15C1.37 7.1 0.41 7.15 0.1 7.15ZM0.58 33.44C0.58 30.88 2.55 28.87 4.99 28.87C7.44 28.87 9.41 30.88 9.41 33.44C9.41 35.992 7.44 38 4.99 38C2.55 38 0.58 35.992 0.58 33.44Z"
              fill="#D1D5DD"
            />
          </svg>
        )}
      </div>
    </MotionWrapper>
  );
};
